<script setup lang="ts">
  import type { Invoice } from '@api/models';
  import { ref } from 'vue';

  import { InvoiceStatusBadge } from '@stuut/shared';
  import { Button } from '@ui/components/button';
  import LoadingSpinner from '@ui/components/LoadingSpinner.vue';
  import { MonetaryText } from '@ui/components/monetary';
  import { QuickView } from '@ui/components/popover';
  import {
    PropertyList,
    PropertyListItem,
    PropertyListItemLabel,
    PropertyListItemValue,
  } from '@ui/components/property-list';
  import { Timestamp } from '@ui/components/time';

  import { stuut } from '@/api';

  const props = defineProps<{
    invoice: Invoice;
  }>();

  const fullInvoice = ref<Invoice>();
  const isLoading = ref(false);

  const loadInvoice = async () => {
    if (fullInvoice.value || isLoading.value) return; // avoid refetch
    isLoading.value = true;
    fullInvoice.value = await stuut.invoices.retrieve(props.invoice.id);
    isLoading.value = false;
  };
</script>

<template>
  <QuickView @opened="loadInvoice">
    <template #trigger>
      <div
        class="w-fit underline decoration-dashed decoration-muted-foreground decoration-1 underline-offset-4 text-nowrap"
        v-bind="$attrs"
      >
        {{ invoice.number }}
      </div>
    </template>

    <template #content>
      <div class="flex flex-col gap-4">
        <div v-if="isLoading" class="text-center">
          <LoadingSpinner class="text-xs" />
        </div>

        <template v-else-if="fullInvoice">
          <div class="flex items-center gap-2">
            <div class="font-semibold">{{ fullInvoice.number }}</div>
            <InvoiceStatusBadge :invoice="fullInvoice" size="xs" />
          </div>

          <PropertyList class="gap-1 text-xs">
            <PropertyListItem>
              <PropertyListItemLabel>Customer</PropertyListItemLabel>
              <PropertyListItemValue class="h-4!">
                {{ fullInvoice.partner.computedName }}
              </PropertyListItemValue>
            </PropertyListItem>

            <PropertyListItem>
              <PropertyListItemLabel>Issued On</PropertyListItemLabel>
              <PropertyListItemValue class="h-4!">
                <Timestamp :time="fullInvoice.issued_at" format="L" date-only />
              </PropertyListItemValue>
            </PropertyListItem>

            <PropertyListItem
              v-if="fullInvoice.pending_amount_remaining === fullInvoice.amount_remaining"
            >
              <PropertyListItemLabel>Balance</PropertyListItemLabel>
              <PropertyListItemValue class="h-4!">
                <MonetaryText
                  class="text-right"
                  :value="fullInvoice.amount_remaining"
                  :currency="fullInvoice.currency"
                  smallest-unit
                  :compact="false"
                />
              </PropertyListItemValue>
            </PropertyListItem>

            <PropertyListItem
              v-if="fullInvoice.pending_amount_remaining !== fullInvoice.amount_remaining"
            >
              <PropertyListItemLabel> Pending Balance </PropertyListItemLabel>
              <PropertyListItemValue class="h-4!">
                <MonetaryText
                  class="text-right"
                  :value="fullInvoice.pending_amount_remaining"
                  :currency="fullInvoice.currency"
                  smallest-unit
                  :compact="false"
                />
              </PropertyListItemValue>
            </PropertyListItem>
          </PropertyList>

          <RouterLink
            :to="{
              name: 'invoices.invoice',
              params: {
                id: invoice.id,
              },
            }"
            @click.stop
          >
            <Button class="inline-block truncate text-left" variant="link" size="inline">
              <span>View Details</span>
              <i class="far fa-arrow-right-long ml-1" />
            </Button>
          </RouterLink>
        </template>
      </div>
    </template>
  </QuickView>
</template>

<style scoped></style>
